// frontend/src/pages/write/SeoAnalyser.jsx
import React, { useState, useEffect } from "react";
import { STAGE_ORDER } from "../../utils/stages";

const getSeoBadge = (score) => {
  if (score >= 80) return { badge: "🟢 Strong", color: "green" };
  if (score >= 50) return { badge: "🟡 Moderate", color: "orange" };
  return { badge: "🔴 Weak", color: "red" };
};

const getTrafficLight = (score, thresholds) => {
  if (typeof score !== "number" || isNaN(score)) return "⚪";

  // Binary (1 = pass)
  if ("green" in thresholds && Object.keys(thresholds).length === 1) {
    return score === thresholds.green ? "🟢" : "🔴";
  }

  // Binary check (1 = pass)
  if (thresholds.green === 1 && thresholds.yellow === undefined) {
    return score === 1 ? "🟢" : "🔴";
  }

  // Range check
  if (score >= thresholds.green) return "🟢";
  if (thresholds.yellow !== undefined && score >= thresholds.yellow) return "🟡";

  // Min/max range
  if ("min" in thresholds && "max" in thresholds) {
    return score >= thresholds.min && score <= thresholds.max ? "🟢" : "🔴";
  }

  // Min-only check
  if ("min" in thresholds) {
    return score >= thresholds.min ? "🟢" : "🔴";
  }

  // Fallback
  return "🔴";
};

// === helpers for robust matching ===
const normalizeText = (str = "") =>
  str
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const containsPhrase = (haystack, phrase) =>
  normalizeText(haystack).includes(normalizeText(phrase));

const filenameFromUrl = (url = "") => {
  try {
    const clean = decodeURI(url.split("?")[0].split("#")[0]);
    const leaf = clean.split("/").pop() || "";
    const noExt = leaf.includes(".") ? leaf.slice(0, leaf.lastIndexOf(".")) : leaf;
    return noExt.replace(/[-_]+/g, " "); // treat - and _ as spaces
  } catch {
    return "";
  }
};

const getCriticalIssues = ({ title, description, keyphrase, slug, body, alt, wordCount }) => {
  const issues = [];

  const plainText = body
    .replace(/<br\s*\/?>/gi, "\n")                 // treat <br> as line break
    .replace(/<\/p>/gi, "\n")                       // treat </p> as line break
    .replace(/<[^>]*>/g, "")                        // remove other tags
    .replace(/&nbsp;/g, " ")                        // decode spaces
    .replace(/\s+/g, " ")                           // collapse whitespace
    .trim();

  const computedWordCount = plainText ? plainText.split(" ").length : 0;

  const hasKeyphrase = typeof keyphrase === "string" && keyphrase.trim().length > 0;
  if (!hasKeyphrase) {
    issues.push("Missing focus keyphrase.");
    return issues; // keep early return here: everything else depends on having a keyphrase
  }

  if ((keyphrase ? keyphrase.trim().split(/\s+/).length : 0) < 3 || (keyphrase ? keyphrase.trim().split(/\s+/).length : 0) > 5) {
    issues.push("Focus keyphrase must be 3-5 words in length.");
  }

  if (!title) {
    issues.push("Missing title.");
  } else if (!title.toLowerCase().includes(keyphrase.toLowerCase())) {
    issues.push("Focus keyphrase missing in title.");
  }

  if (!description) {
    issues.push("Missing meta description.");
  } else if (!description.toLowerCase().includes(keyphrase.toLowerCase())) {
    issues.push("Focus keyphrase missing in meta description.");
  }

    if (!slug) {
    issues.push("Missing URL slug.");
  } else if (!slug.includes(keyphrase.toLowerCase().replace(/\s+/g, "-"))) {
    issues.push("Focus keyphrase missing in URL slug.");
  }

    if (!body) {
    issues.push("Missing content.");
  } else if (computedWordCount < 500) {
    issues.push("Content body must contain at least 500 words.");
  } else if (!body.toLowerCase().includes(keyphrase.toLowerCase())) {
    issues.push("Focus keyphrase missing in content body.");
  }

  const siteDomain = process.env.REACT_APP_SITE_DOMAIN || "forloveofwriters.com";
  const escapedDomain = siteDomain.replace(/\./g, "\\.");
  const internalLinkRegex = new RegExp(`<a\\s+[^>]*href=["'][^"']*${escapedDomain}[^"']*["']`, "i");

  const hasInternalLink = internalLinkRegex.test(body);

  if (!hasInternalLink) {
    issues.push("No internal link found.");
  }

  return issues;
};

export default function SeoAnalyser({
  seoTitle,
  metaDescription,
  focusKeyphrase,
  content,
  postSlug,
  caption,
  previousArticles,
  featuredImageUrl,
  onCriticalIssuesChange,
  currentArticleId,
  stageKey,
  currentUserRole,
}) {
  const [seoScore, setSeoScore] = useState(0);
  const [hasInput, setHasInput] = useState(false);
  const [density, setDensity] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [trafficLights, setTrafficLights] = useState([]);
  const [detailedTrafficLights, setDetailedTrafficLights] = useState({});
  const [criticalIssues, setCriticalIssues] = useState([]);
  const [metricScores, setMetricScores] = useState({});

  const [keyphraseUnique, setKeyphraseUnique] = useState(false);
  const [keyphraseWordCount, setKeyphraseWordCount] = useState(0);
  const [isKeyphraseInTitle, setIsKeyphraseInTitle] = useState(false);
  const [isTitleStartingWithKeyphrase, setIsTitleStartingWithKeyphrase] = useState(false);
  const [isKeyphraseInMeta, setIsKeyphraseInMeta] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isKeyphraseInContent, setIsKeyphraseInContent] = useState(false);
  const [isKeyphraseInFirstParagraph, setIsKeyphraseInFirstParagraph] = useState(false);
  const [subheadingsText, setSubheadingsText] = useState("");
  const [isKeyphraseInSubheadings, setIsKeyphraseInSubheadings] = useState(false);
  const [hasH2, setHasH2] = useState(false);
  const [isKeyphraseInFirstH2, setIsKeyphraseInFirstH2] = useState(false);
  const [normalizedSlug, setNormalizedSlug] = useState("");
  const [isSlugExactMatch, setIsSlugExactMatch] = useState(false);
  const [isSlugPartialMatch, setIsSlugPartialMatch] = useState(false);
  const [firstParagraph, setFirstParagraph] = useState("");
  const [headings, setHeadings] = useState("");
  const [normalizedKeyphraseSlug, setNormalizedKeyphraseSlug] = useState("");
  const [slugWordCount, setSlugWordCount] = useState(0);
  const [imageCount, setImageCount] = useState(0);
  const [matchesImageName, setMatchesImageName] = useState(false);
  const [isAltTextOk, setIsAltTextOk] = useState(false);
  const [hasInternalLink, setHasInternalLink] = useState(false);
  const [hasExternalLink, setHasExternalLink] = useState(false);

  const isWriter = currentUserRole === 2;

  const currentStageIndex = STAGE_ORDER.indexOf(stageKey);
  const revisedDraftIndex = STAGE_ORDER.indexOf("SECOND_DRAFT");

  const writerAtAllowedStage =
    revisedDraftIndex !== -1 &&
    currentStageIndex !== -1 &&
    currentStageIndex >= revisedDraftIndex;

  // Editors/Lead/Admin: always see. Writers: only from Revised Draft (SECOND_DRAFT) onward.
  const canShowSEO = !isWriter || writerAtAllowedStage;

  const stripHtml = (html) =>
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const title = (seoTitle || "").trim();
  const description = (metaDescription || "").trim();
  const keyphrase = (focusKeyphrase || "").trim();
  const body = (content || "").trim();
  const slug = (postSlug || "").trim();
  const alt = (caption || "").trim();

  const calculateSeoScore = () => {

    const hasAnyInput =
      title !== "" ||
      description !== "" ||
      keyphrase !== "" ||
      stripHtml(body) !== "" ||
      slug !== "" ||
      alt !== "";

    setHasInput(hasAnyInput);

    if (!hasAnyInput) {
      setSeoScore(0);
      setSuggestions([]);
      setDensity(0);
      setWordCount(0);
      return;
    }

    const normalize = (s) =>
      (s ?? "")
        .toString()
        .normalize("NFKC")
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase();

    const normalizeSlug = (s) => normalize(s).replace(/\s+/g, "-");
    const currentSlugNorm = normalizeSlug(slug);

    const isSameArticle = (a) => {
      const aid = String(a?.id ?? a?._id ?? a?.postId ?? "");
      const sid = normalizeSlug(a?.post_slug ?? a?.slug ?? a?.postSlug ?? "");
      const curId = currentArticleId ? String(currentArticleId) : "";
      return (curId && aid && aid === curId) || (sid && currentSlugNorm && sid === currentSlugNorm);
    };

    const normalizedKeyphrase = normalize(keyphrase);

    const list = Array.isArray(previousArticles) ? previousArticles : [];

    const isDuplicate = !!normalizedKeyphrase && list.some(a => {
      const k = normalize(a?.focusKeyphrase ?? a?.keyphrase);
      if (!k) return false;             // ignore missing/empty keyphrases
      if (isSameArticle(a)) return false; // ignore the current article
      return k === normalizedKeyphrase;   // exact, case/space/Unicode-normalized
    });

    const keyphraseUniqueComputed = !!normalizedKeyphrase && list.length > 0 && !isDuplicate;
    const keyphraseWordCount = keyphrase ? keyphrase.trim().split(/\s+/).length : 0;
    const isKeyphraseInTitle = keyphrase.trim().length > 0 && title.toLowerCase().includes(keyphrase.toLowerCase());
    const isTitleStartingWithKeyphrase = keyphrase.trim().length > 0 && typeof seoTitle === "string" && seoTitle.toLowerCase().startsWith(keyphrase.toLowerCase());
    const isKeyphraseInMeta = keyphrase.trim().length > 0 && description.toLowerCase().includes(keyphrase.toLowerCase());
    const plainText = body
      .replace(/<br\s*\/?>/gi, "\n")                 // treat <br> as line break
      .replace(/<\/p>/gi, "\n")                       // treat </p> as line break
      .replace(/<[^>]*>/g, "")                        // remove other tags
      .replace(/&nbsp;/g, " ")                        // decode spaces
      .replace(/\s+/g, " ")                           // collapse whitespace
      .trim();
    const computedWordCount = plainText ? plainText.split(" ").length : 0;
    const isKeyphraseInContent = keyphrase.trim().length > 0 && body.toLowerCase().includes(keyphrase.toLowerCase());
    const isKeyphraseInFirstParagraph = keyphrase.trim().length > 0 && content.split("</p>")[0].toLowerCase().includes(keyphrase.toLowerCase());

    const h2Matches = body.match(/<h2\b[^>]*>(.*?)<\/h2>/gis) || [];
    const hasH2Local = h2Matches.length > 0;
    const firstH2Raw = hasH2Local ? h2Matches[0] : "";
    const firstH2Plain = stripHtml(firstH2Raw).toLowerCase();
    const isKeyphraseInFirstH2Local =
      keyphrase.trim().length > 0 && firstH2Plain.includes(keyphrase.toLowerCase());

    const subheadingsText = (body.match(/<h[23][^>]*>(.*?)<\/h[23]>/gis) || [])
      .join(" ")
      .toLowerCase();
    const isKeyphraseInSubheadings = keyphrase.trim().length > 0 && subheadingsText.includes(keyphrase.toLowerCase());
    const normalizedSlug = keyphrase.trim().length > 0 && keyphrase.toLowerCase().trim().replace(/\s+/g, "-");
    const isSlugExactMatch = keyphrase.trim().length > 0 && slug === normalizedSlug;
    const isSlugPartialMatch = keyphrase.trim().length > 0 && slug.includes(normalizedSlug);
    const firstParagraph = content.split("</p>")[0];
    const headings = subheadingsText;

    const normalizedKeyphraseSlug = keyphrase.trim().length > 0 && keyphrase.toLowerCase().trim().replace(/\s+/g, "-");
    const slugWordCount = slug.split("-").filter(Boolean).length;

    // Build image list from body HTML + featured image
    const images = [];

    // From body: collect src + alt (supports data-alt)
    (body.match(/<img\b[^>]*>/gi) || []).forEach(tag => {
      const src = (tag.match(/\bsrc=["']([^"']+)["']/i) || [])[1] || "";
      const altAttr =
        (tag.match(/\balt=["']([^"']*)["']/i) || [])[1] ||
        (tag.match(/\bdata-alt=["']([^"']*)["']/i) || [])[1] ||
        "";
      images.push({ src, alt: altAttr });
    });

    // Featured image (if provided) + caption as alt
    if (featuredImageUrl) {
      images.push({ src: featuredImageUrl, alt: alt });
    }

    const imageCount = images.length;

    // Keyphrase in any image filename
    const matchesImageName = !!keyphrase && images.some(im => {
      const fname = filenameFromUrl(im.src);
      return containsPhrase(fname, keyphrase);
    });

    // Keyphrase in any image alt (body imgs OR featured caption)
    const isAltTextOk = !!keyphrase && images.some(im => containsPhrase(im.alt, keyphrase));

    const hasRelativeInternal = /<a\s+[^>]*href=["']\/(?!\/)[^"']*["']/i.test(body);

    const internalLinkRegex = new RegExp(`<a\\s+[^>]*href=["'][^"']*${(process.env.REACT_APP_SITE_DOMAIN || "forloveofwriters.com").replace(/\./g, "\\.")}[^"']*["']`, "i");
    const hasInternalLink = internalLinkRegex.test(body) || hasRelativeInternal;

    const externalLinkRegex = new RegExp(`<a\\s+[^>]*href=["']https?:\/\/(?!${(process.env.REACT_APP_SITE_DOMAIN || "forloveofwriters.com").replace(/\./g, "\\.")})[^"']*["']`, "i");
    const hasExternalLink = externalLinkRegex.test(body);

    setKeyphraseUnique(keyphraseUniqueComputed);
    setKeyphraseWordCount(keyphraseWordCount);
    setIsKeyphraseInTitle(isKeyphraseInTitle);
    setIsTitleStartingWithKeyphrase(isTitleStartingWithKeyphrase);
    setIsKeyphraseInMeta(isKeyphraseInMeta);
    setWordCount(computedWordCount);
    setIsKeyphraseInContent(isKeyphraseInContent);
    setIsKeyphraseInFirstParagraph(isKeyphraseInFirstParagraph);

    setSubheadingsText(subheadingsText);
    setIsKeyphraseInSubheadings(isKeyphraseInSubheadings);
    setHeadings(headings);

    setHasH2(hasH2Local);
    setIsKeyphraseInFirstH2(isKeyphraseInFirstH2Local);

    setNormalizedSlug(normalizedSlug);
    setIsSlugExactMatch(isSlugExactMatch);
    setIsSlugPartialMatch(isSlugPartialMatch);
    setFirstParagraph(firstParagraph);
    setNormalizedKeyphraseSlug(normalizedKeyphraseSlug);
    setSlugWordCount(slugWordCount);
    setImageCount(imageCount);
    setMatchesImageName(matchesImageName);
    setIsAltTextOk(isAltTextOk);
    setHasInternalLink(hasInternalLink);
    setHasExternalLink(hasExternalLink);

    if (!keyphrase) {
      setSuggestions(["Please enter a focus keyphrase."]);
    }

    let score = 0;
    const maxScore = 100;
    const suggestions = [];
    const metricScores = {}; // key: metric name, value: { score: X, max: Y }

    // Focus keyphrase is unique check
    if (keyphraseUniqueComputed) {
      score += 5;
      metricScores.keyphraseUnique = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseUnique = { score: 0, max: 5 };
      suggestions.push("Choose a unique focus keyphrase (ie. not previously used in another FLOW article).");
    }

    // Keyphrase is 3–5 words
    if (keyphraseWordCount >= 3 && keyphraseWordCount <= 5) {
      score += 5;
      metricScores.keyphraseLength = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseLength = { score: 0, max: 5 };
      suggestions.push(`Your focus keyphrase contains ${keyphraseWordCount} word${keyphraseWordCount !== 1 ? 's' : ''}. It should contain 3 to 5 words.`);
    }

    // Focus keyphrase in SEO title
    if (isKeyphraseInTitle) {
      score += 5;
      metricScores.keyphraseInTitle = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInTitle = { score: 0, max: 5 };
      suggestions.push("Include the focus keyphrase in the SEO title.");
    }

    // Keyphrase starts the SEO title
    if (isTitleStartingWithKeyphrase) {
      score += 5;
      metricScores.titleStartsWithKeyphrase = { score: 5, max: 5 };
    } else {
      metricScores.titleStartsWithKeyphrase = { score: 0, max: 5 };
      suggestions.push("Start your SEO title with the focus keyphrase.");
    }

    // SEO Title length check
    if (title.length >= 40 && title.length <= 60) {
      score += 5;
      metricScores.titleLength = { score: 5, max: 5 };
    } else if (title.length < 40) {
      metricScores.titleLength = { score: 0, max: 5 };
      suggestions.push(`Your title is currently ${title.length} characters long. Increase it to 40-60 characters.`);
    } else {
      metricScores.titleLength = { score: 0, max: 5 };
      suggestions.push(`Your title is currently ${title.length} characters long. Decrease it to 40-60 characters.`);
    }

    // Focus keyphrase in meta description
    if (isKeyphraseInMeta) {
      score += 5;
      metricScores.keyphraseInMeta = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInMeta = { score: 0, max: 5 };
      suggestions.push("Include the focus keyphrase in the meta description.");
    }

    // Meta description length check
    if (description.length >= 120 && description.length <= 156) {
      score += 5;
      metricScores.metaLength = { score: 5, max: 5 };
    } else if (description.length < 120) {
      metricScores.metaLength = { score: 0, max: 5 };
      suggestions.push(`Your meta description is currently ${description.length} characters long. Increase it to 120-156 characters.`);
    } else {
      metricScores.metaLength = { score: 0, max: 5 };
      suggestions.push(`Your meta description is currently ${description.length} characters long. Decrease it to 120-156 characters.`);
    }

    // Content length check
    if (computedWordCount >= 500) {
      score += 5;
      metricScores.wordCount = { score: 5, max: 5 };
    } else {
      metricScores.wordCount = { score: 0, max: 5 };
      suggestions.push("Article must be at least 500 words in length.");
    }

    // Focus keyphrase in content
    if (isKeyphraseInContent) {
      score += 5;
      metricScores.keyphraseInContent = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInContent = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the content body.");
    }

    // Focus keyphrase density check
    if (computedWordCount === 0 || !keyphrase.trim()) {
      setDensity(0);
      suggestions.push("Add content to your article body.");
    } else {
      const escapedKeyphrase = keyphrase.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const keyphraseRegex = new RegExp(`\\b${escapedKeyphrase}\\b`, "gi");
      const keyphraseCount = (plainText.match(keyphraseRegex) || []).length;

      const density = (keyphraseCount / computedWordCount) * 100;
      setDensity(density);

      if (density >= 0.5 && density <= 2.5) {
        score += 5;
        metricScores.keyphraseDensity = { score: 5, max: 5 };
      } else if (density > 2.5) {
        metricScores.keyphraseDensity = { score: 0, max: 5 };
        suggestions.push(`Keyphrase density is too high (${density.toFixed(2)}%). Consider reducing the frequency of your focus keyphrase to avoid keyword stuffing.`);
      } else {
        metricScores.keyphraseDensity = { score: 0, max: 5 };
        suggestions.push(`Keyphrase density is too low (${density.toFixed(2)}%). Consider including your focus keyphrase more frequently.`);
      }
    }

    // Focus keyphrase in first paragraph
    if (isKeyphraseInFirstParagraph) {
      score += 5;
      metricScores.keyphraseInFirstParagraph = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInFirstParagraph = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the first paragraph.");
    }

    // Keyphrase in first H2 Heading (title is H1; in-body starts at H2)
    if (hasH2 && isKeyphraseInFirstH2) {
      score += 5;
      metricScores.keyphraseInFirstH2 = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInFirstH2 = { score: 0, max: 5 };
      if (!hasH2) {
        suggestions.push("Add an H2 as the first in-body heading (title is already the H1).");
      } else {
        suggestions.push("Include the focus keyphrase in the first H2 heading (when natural).");
      }
    }

    // Headings check & Focus keyphrase in subheadings
    if (!headings) {
      metricScores.keyphraseInSubheadings = { score: 0, max: 5 };
      suggestions.push("Use H2 or H3 subheadings that include your focus keyphrase.");
    } else if (!isKeyphraseInSubheadings) {
      metricScores.keyphraseInSubheadings = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in at least one H2 or H3 subheading.");
    } else {
      score += 5;
      metricScores.keyphraseInSubheadings = { score: 5, max: 5 };
    }

    // URL slug check
    if (isSlugExactMatch) {
      score += 5;
      metricScores.slugUrl = { score: 5, max: 5 };
    } else if (isSlugPartialMatch) {
      score += 3;
      metricScores.slugUrl = { score: 3, max: 5 };
      suggestions.push("Make your slug match the focus keyphrase exactly, if possible.");
    } else {
      metricScores.slugUrl = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the URL slug. If possible, make it match the focus keyphrase exactly.");
    }

    // Slug length is 3–5 words
    if (slugWordCount >= 3 && slugWordCount <= 5) {
      score += 5;
      metricScores.slugLength = { score: 5, max: 5 };
    } else {
      metricScores.slugLength = { score: 0, max: 5 };
      suggestions.push("Use a short slug of 3–5 words for better readability and SEO.");
    }

    // Image count check
    const minImages = Math.max(1, Math.floor(computedWordCount / 500));
    if (imageCount >= minImages) {
      score += 5;
      metricScores.imageCount = { score: 5, max: 5 };
    } else {
      metricScores.imageCount = { score: 0, max: 5 };
      suggestions.push("Insert at least one image for every 500 words (including the featured image).");
    }

    // Image filename includes keyphrase
    if (matchesImageName) {
      score += 5;
      metricScores.keyphraseImageFilename = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseImageFilename = { score: 0, max: 5 };
      suggestions.push("Include the focus keyphrase in your image filename.");
    }

    // Image alt text check
    if (isAltTextOk) {
      score += 5;
      metricScores.imageAltText = { score: 5, max: 5 };
    } else {
      metricScores.imageAltText = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the image alt text. Ensure alt text is descriptive; include the keyphrase only if it accurately describes the image.");
    }

    // Internal link check
    if (hasInternalLink) {
      score += 5;
      metricScores.internalLink = { score: 5, max: 5 };
    } else {
      metricScores.internalLink = { score: 0, max: 5 };
      suggestions.push("Add at least one internal link to your content.");
    }

    // External link check
    if (hasExternalLink) {
      score += 5;
      metricScores.externalLink = { score: 5, max: 5 };
    } else {
      metricScores.externalLink = { score: 0, max: 5 };
      suggestions.push("Add at least one external link to your content.");
    }

    setSeoScore(Math.round(Math.max(0, Math.min(score, maxScore))));

    setSuggestions(suggestions);
    setMetricScores(metricScores);

    const thresholds = {
      keyphraseUnique: { green: 1 },
      keyphraseLength: { min: 3, max: 5 }, // Ideal: 3–5
      keyphraseInTitle: { green: 1 },
      titleStartsWithKeyphrase: { green: 1 },
      titleLength: { min: 40, max: 60 },
      keyphraseInMeta: { green: 1 },
      metaLength: { min: 120, max: 156 },
      wordCount: { min: 500 },
      keyphraseInContent: { green: 1 },
      keyphraseDensity: { min: 0.5, max: 2.5 },
      keyphraseInFirstParagraph: { green: 1 },
      keyphraseInFirstH2: { green: 1 },
      keyphraseInSubheadings: { green: 1 },
      slugUrl: { green: 1 },
      slugLength: { min: 3, max: 5 },
      imageCount: { min: minImages },
      keyphraseImageFilename: { green: 1 },
      imageAltText: { green: 1 },
      internalLink: { green: 1 },
      externalLink: { green: 1 },
    };

    const inputValues = {
      keyphraseUnique: keyphraseUniqueComputed ? 1 : 0,
      keyphraseLength: keyphraseWordCount,
      keyphraseInTitle: Number(isKeyphraseInTitle),
      titleStartsWithKeyphrase: Number(isTitleStartingWithKeyphrase),
      titleLength: title.length,
      keyphraseInMeta: Number(isKeyphraseInMeta),
      metaLength: description.length,
      wordCount: computedWordCount,
      keyphraseInContent: Number(isKeyphraseInContent),
      keyphraseDensity: density,
      keyphraseInFirstParagraph: Number(isKeyphraseInFirstParagraph),
      keyphraseInFirstH2: Number(hasH2 && isKeyphraseInFirstH2),
      keyphraseInSubheadings: Number(isKeyphraseInSubheadings),
      slugUrl: Number(isSlugExactMatch || isSlugPartialMatch),
      slugLength: slugWordCount,
      imageCount: imageCount,
      keyphraseImageFilename: Number(matchesImageName),
      imageAltText: Number(isAltTextOk),
      internalLink: Number(hasInternalLink),
      externalLink: Number(hasExternalLink),
    };

    const seoTrafficLights = Object.fromEntries(
      Object.entries(inputValues).map(([key, value]) => [
        key,
        getTrafficLight(value, thresholds[key]),
      ])
    );

    setDetailedTrafficLights(seoTrafficLights);
    setTrafficLights(Object.values(seoTrafficLights));

    const criticalIssues = getCriticalIssues({
      title,
      description,
      keyphrase,
      slug,
      body,
      alt,
    });

    setCriticalIssues(criticalIssues);
  };

  useEffect(() => {
    calculateSeoScore();
  }, [title, description, keyphrase, body, slug, alt, previousArticles, featuredImageUrl, currentArticleId]);

  // Notify parent whenever the list changes
  useEffect(() => {
    if (typeof onCriticalIssuesChange === "function") {
      onCriticalIssuesChange(criticalIssues);
    }
  }, [criticalIssues, onCriticalIssuesChange]);

  const summary = getSeoBadge(seoScore);

  const uniquenessDisplay =
    !keyphrase
      ? "No keyphrase"
      : (Array.isArray(previousArticles) && previousArticles.length > 0)
        ? (keyphraseUnique ? "Yes" : "No")
        : "Checking…";

  const styles = {
    sectionHeader: {
      fontWeight: "bold",
      fontStyle: "italic",
      margin: "16px 0 8px",
      paddingTop: "8px",
      borderTop: "1px solid #e5e5e5",
      color: "#111"
    },
    list: { listStyle: "none", paddingLeft: 0, margin: 0 },
    metricRow: { display: "flex", gap: "10px", marginBottom: "10px", alignItems: "flex-start" },
    icon: { minWidth: 24, textAlign: "center", fontSize: "1.1rem", lineHeight: "1.2rem" },
    content: { lineHeight: 1.25 },
    titleLine: { fontWeight: "600" },
    metaLine: { color: "#666", fontSize: "0.85rem", marginTop: 2 }
  };

  const MetricRow = ({ light, title, value, meta }) => (
    <li style={styles.metricRow}>
      <span style={styles.icon}>{light ?? "⚪"}</span>
      <div style={styles.content}>
        <div style={styles.titleLine}><strong>{title}:</strong> {value}</div>
        {meta && <div style={styles.metaLine}>{meta}</div>}
      </div>
    </li>
  );

  return (
    <div className="seo-score-container">
      <div style={{ marginTop: "10px", marginBottom: "10px", fontWeight: "bold", fontSize: "16px", color: "#000" }}>
        SEO Score:{" "}
        {!hasInput ? (
          <span
            style={{
              fontStyle: "italic",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#555",
            }}
          >
            Start adding content to see your SEO score.
          </span>
        ) : !canShowSEO ? (
          <span
            style={{
              fontStyle: "italic",
              fontSize: "16px",
              fontWeight: "normal",
              color: "#555",
            }}
          >
            Your SEO score will be displayed once your draft reaches the
            Revised Draft stage.
          </span>
        ) : (
          <span style={{ color: summary.color }}>
            {summary.badge} ({seoScore}%)
          </span>
        )}
      </div>
      {hasInput && canShowSEO && (
      <div className="seo-recommendations">

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "12px",
            background: "#fafafa",
            border: "1px solid #ddd",
            borderRadius: "6px",
            padding: "6px 10px",
            margin: "4px 0 14px 0",
            fontWeight: "500",
            fontSize: "0.9rem",
          }}
        >
          <span style={{ fontWeight: "700" }}>At a Glance:</span>
          <span>🟢 {Object.values(detailedTrafficLights).filter(v => v === "🟢").length}</span>
          <span>🟡 {Object.values(detailedTrafficLights).filter(v => v === "🟡").length}</span>
          <span>🔴 {Object.values(detailedTrafficLights).filter(v => v === "🔴").length}</span>
        </div>

        {hasInput && criticalIssues.length > 0 && (
          <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px" }}>
            ⚠️ Critical Issues Found – Must be addressed before publishing:
          </li>
        )}
        {criticalIssues.length > 0 && (
          <ul style={{ fontWeight: "normal", marginTop: "10px" }}>
            {criticalIssues.map((issue, i) => (
              <li key={i}>{issue}</li>
            ))}
          </ul>
        )}

      <li style={{ listStyleType: "none", fontWeight: "bold", margin: "20px 0 4px" }}>Scorecard:</li>
      <ul style={styles.list}>

        <li style={styles.sectionHeader}>Keyphrase</li>
        <MetricRow
          light={detailedTrafficLights.keyphraseUnique ?? "⚪"}
          title="Keyphrase is Unique"
          value={uniquenessDisplay}
          meta={`Score ${metricScores.keyphraseUnique?.score ?? 0}/${metricScores.keyphraseUnique?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.keyphraseLength ?? "⚪"}
          title="Keyphrase Length"
          value={`${keyphraseWordCount} words`}
          meta={`Score ${metricScores.keyphraseLength?.score ?? 0}/${metricScores.keyphraseLength?.max ?? 5} • Ideal: 3–5 words`}
        />

        <li style={styles.sectionHeader}>SEO Title</li>
        <MetricRow
          light={detailedTrafficLights.keyphraseInTitle ?? "⚪"}
          title="Keyphrase in SEO Title"
          value={isKeyphraseInTitle ? "Yes" : "No"}
          meta={`Score ${metricScores.keyphraseInTitle?.score ?? 0}/${metricScores.keyphraseInTitle?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.titleStartsWithKeyphrase ?? "⚪"}
          title="SEO Title Starts with Keyphrase"
          value={isTitleStartingWithKeyphrase ? "Yes" : "No"}
          meta={`Score ${metricScores.titleStartsWithKeyphrase?.score ?? 0}/${metricScores.titleStartsWithKeyphrase?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.titleLength ?? "⚪"}
          title="SEO Title Length"
          value={`${title.length} characters`}
          meta={`Score ${metricScores.titleLength?.score ?? 0}/${metricScores.titleLength?.max ?? 5} • Ideal: 40-60 characters`}
        />

        <li style={styles.sectionHeader}>Meta Description</li>
        <MetricRow
          light={detailedTrafficLights.keyphraseInMeta ?? "⚪"}
          title="Keyphrase in Meta Description"
          value={isKeyphraseInMeta ? "Yes" : "No"}
          meta={`Score ${metricScores.keyphraseInMeta?.score ?? 0}/${metricScores.keyphraseInMeta?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.metaLength ?? "⚪"}
          title="Meta Description Length"
          value={`${description.length} characters`}
          meta={`Score ${metricScores.metaLength?.score ?? 0}/${metricScores.metaLength?.max ?? 5} • Ideal: 120-156 characters`}
        />

        <li style={styles.sectionHeader}>Content</li>
        <MetricRow
          light={detailedTrafficLights.wordCount ?? "⚪"}
          title="Word Count"
          value={`${wordCount} words`}
          meta={`Score ${metricScores.wordCount?.score ?? 0}/${metricScores.wordCount?.max ?? 5} • Minimum: 500 words`}
        />
        <MetricRow
          light={detailedTrafficLights.keyphraseInContent ?? "⚪"}
          title="Keyphrase in Body Content"
          value={isKeyphraseInContent ? "Yes" : "No"}
          meta={`Score ${metricScores.keyphraseInContent?.score ?? 0}/${metricScores.keyphraseInContent?.max ?? 5} • Ideal: Yes`}
        />

        <MetricRow
          light={detailedTrafficLights.keyphraseDensity ?? "⚪"}
          title="Keyphrase Density"
          value={`${density.toFixed(2)}%`}
          meta={`Score ${metricScores.keyphraseDensity?.score ?? 0}/${metricScores.keyphraseDensity?.max ?? 5} • Ideal: 0.5–2.5%`}
        />

        <MetricRow
          light={detailedTrafficLights.keyphraseInFirstParagraph ?? "⚪"}
          title="Keyphrase in First Paragraph"
          value={isKeyphraseInFirstParagraph ? "Yes" : "No"}
          meta={`Score ${metricScores.keyphraseInFirstParagraph?.score ?? 0}/${metricScores.keyphraseInFirstParagraph?.max ?? 5} • Ideal: Yes`}
        />

        <li style={styles.sectionHeader}>Headings</li>
        <MetricRow
          light={detailedTrafficLights.keyphraseInFirstH2 ?? "⚪"}
          title="Keyphrase in First H2"
          value={hasH2 ? (isKeyphraseInFirstH2 ? "Yes" : "No") : "No H2"}
          meta={`Score ${metricScores.keyphraseInFirstH2?.score ?? 0}/${metricScores.keyphraseInFirstH2?.max ?? 5} • Ideal: Yes • Tip: Title is H1. First heading in body should be H2.`}
        />
        <MetricRow
          light={detailedTrafficLights.keyphraseInSubheadings ?? "⚪"}
          title="Keyphrase in H2/H3 Subheadings"
          value={isKeyphraseInSubheadings ? "Yes" : "No"}
          meta={`Score ${metricScores.keyphraseInSubheadings?.score ?? 0}/${metricScores.keyphraseInSubheadings?.max ?? 5} • Ideal: Yes`}
        />

        <li style={styles.sectionHeader}>URL Slug</li>
        <MetricRow
          light={detailedTrafficLights.slugUrl ?? "⚪"}
          title="Slug Contains Keyphrase"
          value={isSlugExactMatch || isSlugPartialMatch ? "Yes" : "No"}
          meta={`Score ${metricScores.slugUrl?.score ?? 0}/${metricScores.slugUrl?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.slugLength ?? "⚪"}
          title="Slug Word Count"
          value={`${slugWordCount} words`}
          meta={`Score ${metricScores.slugLength?.score ?? 0}/${metricScores.slugLength?.max ?? 5} • Ideal: 3-5 words`}
        />

        <li style={styles.sectionHeader}>Images</li>
        <MetricRow
          light={detailedTrafficLights.imageCount ?? "⚪"}
          title="Image Count"
          value={imageCount}
          meta={`Score ${metricScores.imageCount?.score ?? 0}/${metricScores.imageCount?.max ?? 5} • Ideal: At least ${Math.max(1, Math.floor(wordCount / 500))} image`}
        />
        <MetricRow
          light={detailedTrafficLights.keyphraseImageFilename ?? "⚪"}
          title="Keyphrase in Image Filename"
          value={matchesImageName ? "Yes" : "No"}
          meta={`Score ${metricScores.keyphraseImageFilename?.score ?? 0}/${metricScores.keyphraseImageFilename?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.imageAltText ?? "⚪"}
          title="Keyphrase in Image Alt Text"
          value={isAltTextOk ? "Yes" : "No"}
          meta={`Score ${metricScores.imageAltText?.score ?? 0}/${metricScores.imageAltText?.max ?? 5} • Ideal: Yes`}
        />

        <li style={styles.sectionHeader}>Links</li>
        <MetricRow
          light={detailedTrafficLights.internalLink ?? "⚪"}
          title="Internal Link Present"
          value={hasInternalLink ? "Yes" : "No"}
          meta={`Score ${metricScores.internalLink?.score ?? 0}/${metricScores.internalLink?.max ?? 5} • Ideal: Yes`}
        />
        <MetricRow
          light={detailedTrafficLights.externalLink ?? "⚪"}
          title="External Link Present"
          value={hasExternalLink ? "Yes" : "No"}
          meta={`Score ${metricScores.externalLink?.score ?? 0}/${metricScores.externalLink?.max ?? 5} • Ideal: Yes`}
        />

        {suggestions.length > 0 && (
          <>
            <li style={styles.sectionHeader}>Suggestions</li>
              {suggestions.map((r, i) => (
                <li key={i} style={{ ...styles.metricRow, marginTop: 12 }}>
                  <span style={styles.icon}>💡</span>
                  <div>{r}</div>
                </li>
              ))}
          </>
        )}
      </ul>
      </div>
      )}
    </div>
  );
}
